// The screen's language: decided once at start; a change reloads the page.
//
// Promise:
// - Order: the account's language (a copy kept in localStorage so the first
//   paint is already right) → the browser's languages → English.
// - `syncAccountLocale` is called with the account's value each time the
//   server answers the profile (never with the cached profile restored after a
//   reload, which may still hold the language before a change: the two would
//   reload each other forever); when it differs from the copy, the copy is
//   updated and the page reloads in the right language. If the copy cannot be
//   stored, it does not reload (that would loop).
import {
  isLocale,
  type Locale,
  overwriteGetLocale,
  resolveLocale,
} from "@trameria/i18n";

const KEY = "trameria.locale";
let current: Locale | null = null;

export function chooseLocale(
  saved: string | null,
  languages: readonly string[],
): Locale {
  if (isLocale(saved)) return saved;
  return resolveLocale(languages, "en");
}

function readStored(): Locale | null {
  try {
    const v = localStorage.getItem(KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
}

function writeStored(value: Locale | null): void {
  try {
    if (value) localStorage.setItem(KEY, value);
    else localStorage.removeItem(KEY);
  } catch {
    // Cannot store: syncAccountLocale reads back and skips the reload.
  }
}

const browserLanguages = (): readonly string[] =>
  navigator.languages?.length ? navigator.languages : [navigator.language];

export function initLocale(): Locale {
  if (current) return current;
  const locale = chooseLocale(readStored(), browserLanguages());
  current = locale;
  overwriteGetLocale(() => locale);
  document.documentElement.lang = locale;
  return locale;
}

export function syncAccountLocale(
  saved: string | null | undefined,
  reload: () => void = () => location.reload(),
): void {
  const want = isLocale(saved) ? saved : null;
  if (want === readStored()) return;
  writeStored(want);
  if (want !== readStored()) return;
  if (chooseLocale(want, browserLanguages()) !== current) reload();
}
